

import { generateColors } from "../../../../../utils/chartColors";

function configs(labels, datasets, scaleType = "date", stacked = false, yAxisTitle = "") {
  // Calculate dynamic min, max, and step size for Y-axis
  let allValues = [];

  if (stacked) {
    // If stacked, we need the max sum of each column
    const length = datasets[0]?.data?.length || 0;
    const sums = new Array(length).fill(0);
    datasets.forEach(ds => {
      ds.data.forEach((val, i) => {
        sums[i] += (val || 0);
      });
    });
    allValues = sums;
  } else {
    allValues = datasets.flatMap(dataset => dataset.data);
  }

  const minValue = Math.min(...allValues, 0); // Ensure 0 is included
  const maxValue = Math.max(...allValues);

  const range = maxValue - minValue;
  const stepSize = range === 0 ? 10 : Math.ceil(range / 5); // Divide range into 5 equal steps (Default to 10 if flat)

  // console.log("Incoming datasets : ", datasets);

  // 🟦 Apply Colors Using Profile IDs
  const profileIds = datasets.map(dataset => dataset.profileId || dataset.label);
  const colors = generateColors(datasets.length, profileIds);

  // ❌ REMOVED createDottedPattern function.
  // This logic is now handled in VerticalBarChart/index.js

  // 🟩 Attach Colors to Datasets
  const coloredDatasets = datasets.map((dataset, index) => {
    // Base color from generator
    const baseColor = colors[index];

    let backgroundColor = dataset.backgroundColor || baseColor;

    // ✅ Handle Custom Opacity (Safe Mode for internal use)
    if (dataset.fillOpacity !== undefined) {
      try {
        const r = parseInt(baseColor.slice(1, 3), 16);
        const g = parseInt(baseColor.slice(3, 5), 16);
        const b = parseInt(baseColor.slice(5, 7), 16);
        if (!isNaN(r) && !isNaN(g) && !isNaN(b)) {
          backgroundColor = `rgba(${r}, ${g}, ${b}, ${dataset.fillOpacity})`;
        }
      } catch (e) {
        console.warn("Error converting hex to rgba", e);
      }
    }

    return {
      ...dataset,
      borderColor: dataset.borderColor || baseColor, // Keep border solid
      borderWidth: 1,
      backgroundColor: backgroundColor,
    };
  });


  return {
    data: {
      labels,
      datasets: coloredDatasets,
    },
    options: {
      responsive: true,
      maintainAspectRatio: false,
      layout: {
        padding: { top: 0, bottom: 0 },
      },
      plugins: {
        tooltip: {
          mode: "index",
          intersect: false,
          callbacks: {
            // This will be overridden by the callback in VerticalBarChart/index.js
            label: function (tooltipItem) {
              const dataset = tooltipItem.dataset;
              const profileName = dataset.label || "Unknown Profile"; // Fallback if name is missing
              const value = tooltipItem.raw?.toLocaleString();
              return `${profileName}: ${value}`;
            },
          },
        },
        legend: { display: false },
      },
      scales: {
        x: {
          offset: true,
          ticks: {
            autoSkip: false, // Disable default auto-skip
            maxRotation: 0,
            minRotation: 0,
            align: "center",
            padding: 10,
            callback: function (value, index, values) {
              const rawLabel = this.getLabelForValue(value);

              // 🛑 If not a date scale, return raw label immediately
              if (scaleType !== "date") return rawLabel;

              const totalLabels = values.length; // Total number of labels
              const chartWidth = this.chart.width; // Width of the chart canvas
              const maxVisibleLabels = Math.floor(chartWidth / 50); // Approx. 50px per label

              if (totalLabels <= maxVisibleLabels) {
                // If labels fit without skipping
                const date = new Date(rawLabel);
                if (isNaN(date)) return rawLabel;
                return date.toLocaleDateString('en-GB', {
                  day: '2-digit',
                  month: 'short',
                });
              }

              // Calculate dynamic skipping interval
              const skip = Math.ceil(totalLabels / maxVisibleLabels);

              if (index % skip === 0) {
                const date = new Date(rawLabel);
                if (isNaN(date)) return rawLabel;
                return date.toLocaleDateString('en-GB', {
                  day: '2-digit',
                  month: 'short',
                });
              }

              return ""; // Skip this label
            },
          },
          grid: {
            drawBorder: false,
            drawOnChartArea: false,
            drawTicks: true,
            tickLength: 5,
          },
        },
        y: {
          display: true,
          title: {
            display: !!yAxisTitle,
            text: yAxisTitle,
            color: "#999",
            font: {
              size: 12,
              style: "normal",
              weight: "bold",
            },
            padding: { bottom: 0 },
          },
          min: Math.floor(minValue / stepSize) * stepSize,
          max: Math.ceil(maxValue / stepSize) * stepSize,
          ticks: {
            stepSize,
            callback: value => value.toLocaleString(),
          },
          grid: {
            drawBorder: false,
            tickLength: 0,
            borderDash: [5, 5],
            color: "#c1c4ce5c",
          },
        },
      },
    },
  };
}

export default configs;